if (!customElements.get('product-card-gallery')) {
  class ProductCardGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.track = this.querySelector('[data-gallery-track]');
      this.slides = Array.from(
        this.querySelectorAll('[data-gallery-slide]')
      );
      this.dots = Array.from(
        this.querySelectorAll('[data-gallery-dot]')
      );

      if (!this.track || this.slides.length === 0) return;

      this.initialized = true;
      this.activeIndex = -1;
      this.dragged = false;
      this.ignoreClickUntil = 0;
      this.previewOriginal = null;
      this.scrollFrame = 0;
      this.dragFrame = 0;
      this.pendingScrollLeft = 0;

      this.track.addEventListener(
        'scroll',
        () => this.onScroll(),
        { passive: true }
      );

      this.track.addEventListener(
        'pointerdown',
        (event) => this.onPointerDown(event)
      );

      // Prevent the browser's native image/link dragging.
      this.track.addEventListener(
        'dragstart',
        (event) => event.preventDefault()
      );

      this.track.addEventListener(
        'click',
        (event) => {
          const productLink = event.target.closest(
            '[data-gallery-slide]'
          );

          if (!productLink) return;

          const wasDragged =
            this.dragged ||
            performance.now() < this.ignoreClickUntil;

          if (wasDragged) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }

          // Preserve Ctrl, Cmd, Shift and non-left-click behaviour.
          if (
            event.button !== 0 ||
            event.ctrlKey ||
            event.metaKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();

          window.location.assign(productLink.href);
        },
        true
      );

      this.dots.forEach((dot) => {
        dot.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          this.goTo(
            Number(dot.dataset.galleryDot),
            true
          );
        });
      });

      this.setActive(0);
    }

    disconnectedCallback() {
      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
      }

      if (this.dragFrame) {
        window.cancelAnimationFrame(this.dragFrame);
      }
    }

    onScroll() {
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = 0;

        const width = this.track.clientWidth;
        if (!width) return;

        const index = Math.round(
          this.track.scrollLeft / width
        );

        this.setActive(index);
        this.preloadAround(index);
      });
    }

    onPointerDown(event) {
      this.preloadAround(this.activeIndex);

      // Touch and pen devices use native momentum scrolling.
      if (
        !event.isPrimary ||
        event.pointerType !== 'mouse' ||
        event.button !== 0
      ) {
        return;
      }

      const startLink = event.target.closest(
        '[data-gallery-slide]'
      );

      const startUrl = startLink?.href || '';

      const hasModifierKey =
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey;

      // Prevent the browser from dragging the image or link.
      if (!hasModifierKey) {
        event.preventDefault();
      }

      const startX = event.clientX;
      const startY = event.clientY;
      const startScrollLeft = this.track.scrollLeft;
      const trackWidth = this.track.clientWidth;

      const startIndex = trackWidth
        ? Math.round(startScrollLeft / trackWidth)
        : 0;

      let lastX = startX;
      let horizontalDrag = false;
      let finished = false;

      this.dragged = false;

      try {
        this.track.setPointerCapture(event.pointerId);
      } catch (error) {
        // Continue without pointer capture when unsupported.
      }

      const move = (moveEvent) => {
        const distanceX =
          moveEvent.clientX - startX;

        const distanceY =
          moveEvent.clientY - startY;

        lastX = moveEvent.clientX;

        if (
          !horizontalDrag &&
          Math.abs(distanceX) > 6 &&
          Math.abs(distanceX) > Math.abs(distanceY)
        ) {
          horizontalDrag = true;
          this.dragged = true;

          this.track.classList.add('is-dragging');
        }

        if (!horizontalDrag) return;

        moveEvent.preventDefault();

        this.pendingScrollLeft =
          startScrollLeft - distanceX;

        if (this.dragFrame) return;

        this.dragFrame =
          window.requestAnimationFrame(() => {
            this.dragFrame = 0;

            this.track.scrollLeft =
              this.pendingScrollLeft;
          });
      };

      const finish = (finishEvent) => {
        if (finished) return;

        finished = true;

        if (this.dragFrame) {
          window.cancelAnimationFrame(
            this.dragFrame
          );

          this.dragFrame = 0;

          this.track.scrollLeft =
            this.pendingScrollLeft;
        }

        this.track.classList.remove(
          'is-dragging'
        );

        this.track.removeEventListener(
          'pointermove',
          move
        );

        this.track.removeEventListener(
          'pointerup',
          finish
        );

        this.track.removeEventListener(
          'pointercancel',
          finish
        );

        this.track.removeEventListener(
          'lostpointercapture',
          finish
        );

        // No horizontal movement means it was a click.
        if (!horizontalDrag) {
          this.dragged = false;

          if (
            finishEvent.type === 'pointerup' &&
            startUrl &&
            !hasModifierKey
          ) {
            this.ignoreClickUntil =
              performance.now() + 250;

            window.location.assign(startUrl);
          }

          return;
        }

        // Horizontal movement means it was a drag.
        const distance = startX - lastX;

        let targetIndex = Math.round(
          this.track.scrollLeft /
            this.track.clientWidth
        );

        if (Math.abs(distance) > 45) {
          targetIndex =
            startIndex +
            (distance > 0 ? 1 : -1);
        }

        this.ignoreClickUntil =
          performance.now() + 250;

        this.dragged = false;

        this.goTo(targetIndex, true);
      };

      this.track.addEventListener(
        'pointermove',
        move
      );

      this.track.addEventListener(
        'pointerup',
        finish
      );

      this.track.addEventListener(
        'pointercancel',
        finish
      );

      this.track.addEventListener(
        'lostpointercapture',
        finish
      );
    }

    preloadAround(index) {
      [index - 1, index, index + 1].forEach(
        (slideIndex) => {
          const image =
            this.slides[slideIndex]?.querySelector(
              'img'
            );

          if (!image) return;

          image.loading = 'eager';

          image.decode?.().catch(() => {});
        }
      );
    }

    goTo(index, smooth = false) {
      const safeIndex = Math.max(
        0,
        Math.min(
          index,
          this.slides.length - 1
        )
      );

      const reducedMotion =
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

      this.preloadAround(safeIndex);

      this.track.scrollTo({
        left:
          safeIndex *
          this.track.clientWidth,

        behavior:
          smooth && !reducedMotion
            ? 'smooth'
            : 'auto',
      });

      this.setActive(safeIndex);
    }

    setActive(index) {
      const safeIndex = Math.max(
        0,
        Math.min(
          index,
          this.slides.length - 1
        )
      );

      if (safeIndex === this.activeIndex) {
        return;
      }

      this.activeIndex = safeIndex;

      this.slides.forEach(
        (slide, slideIndex) => {
          slide.classList.toggle(
            'is-active',
            slideIndex === safeIndex
          );
        }
      );

      this.dots.forEach(
        (dot, dotIndex) => {
          const active =
            dotIndex === safeIndex;

          dot.classList.toggle(
            'is-active',
            active
          );

          if (active) {
            dot.setAttribute(
              'aria-current',
              'true'
            );
          } else {
            dot.removeAttribute(
              'aria-current'
            );
          }
        }
      );
    }

    previewImage(
      source,
      sourceSet,
      alt
    ) {
      const image =
        this.slides[0]?.querySelector('img');

      if (!image || !source) return;

      if (!this.previewOriginal) {
        this.previewOriginal = {
          src:
            image.getAttribute('src') ||
            '',

          srcset:
            image.getAttribute(
              'srcset'
            ) || '',

          alt:
            image.getAttribute('alt') ||
            '',
        };
      }

      this.goTo(0);

      image.src = source;

      if (sourceSet) {
        image.srcset = sourceSet;
      } else {
        image.removeAttribute('srcset');
      }

      image.alt = alt || '';
    }

    restorePreview() {
      const image =
        this.slides[0]?.querySelector('img');

      if (
        !image ||
        !this.previewOriginal
      ) {
        return;
      }

      image.src =
        this.previewOriginal.src;

      image.srcset =
        this.previewOriginal.srcset;

      image.alt =
        this.previewOriginal.alt;

      this.previewOriginal = null;
    }
  }

  customElements.define(
    'product-card-gallery',
    ProductCardGallery
  );
}

const findProductCardGallery = (swatch) =>
  swatch
    .closest('.card-wrapper')
    ?.querySelector(
      'product-card-gallery'
    );

document.addEventListener(
  'pointerover',
  (event) => {
    const swatch = event.target.closest(
      '[data-colour-preview]'
    );

    if (
      !swatch ||
      swatch.contains(event.relatedTarget)
    ) {
      return;
    }

    findProductCardGallery(
      swatch
    )?.previewImage(
      swatch.dataset.previewSrc,
      swatch.dataset.previewSrcset,
      swatch.dataset.previewAlt
    );
  }
);

document.addEventListener(
  'pointerout',
  (event) => {
    const swatch = event.target.closest(
      '[data-colour-preview]'
    );

    if (
      !swatch ||
      swatch.contains(event.relatedTarget)
    ) {
      return;
    }

    findProductCardGallery(
      swatch
    )?.restorePreview();
  }
);

document.addEventListener(
  'focusin',
  (event) => {
    const swatch = event.target.closest(
      '[data-colour-preview]'
    );

    if (!swatch) return;

    findProductCardGallery(
      swatch
    )?.previewImage(
      swatch.dataset.previewSrc,
      swatch.dataset.previewSrcset,
      swatch.dataset.previewAlt
    );
  }
);

document.addEventListener(
  'focusout',
  (event) => {
    const swatch = event.target.closest(
      '[data-colour-preview]'
    );

    if (!swatch) return;

    findProductCardGallery(
      swatch
    )?.restorePreview();
  }
);